import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import { useActiveOrg, GateHeader } from "common-fe";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { handleLogout } from "../util/logout";

// AC#4 (#397): only a Fox Village super admin OR a member of a score-entitled
// org may use scoreadmin. Entitlement is Layer 2 (licensing, AUTHZ.md §2): the
// access token carries an `entitlements` claim — a JSON per-org map
// { orgId: [appKeys] } stamped by the account PreTokenGeneration trigger. The
// API independently enforces the same gate (#400); this is the matching FE
// affordance so a non-entitled user gets a clear message, not empty screens.
function activeOrgEntitledToScore(accessToken, activeOrgId) {
  if (!accessToken || !activeOrgId) return false;
  try {
    const claims = jwtDecode(accessToken);
    const raw = claims.entitlements;
    const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
    const apps = map[activeOrgId];
    return Array.isArray(apps) && apps.includes("score");
  } catch {
    return false;
  }
}

// #531 — hard-gate the admin shell on the score-admin ROLE, not just the org's
// entitlement. Entitlement is Layer 2 (org licensing); a scribe-only member of
// a score-entitled org must NOT reach the admin UI and hit a 403 on the first
// write. The access token carries a `roles` claim: a per-org map
// { orgId: [roleNames] } stamped by the account PreTokenGeneration trigger.
// Match qualified ("score:score_admin") or bare ("score_admin"). The API
// independently enforces score:assign_scribes (#401); this is the FE affordance.
function activeOrgHasScoreAdminRole(accessToken, activeOrgId) {
  if (!accessToken || !activeOrgId) return false;
  try {
    const claims = jwtDecode(accessToken);
    const raw = claims.roles;
    const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
    const roles = Array.isArray(map[activeOrgId]) ? map[activeOrgId] : [];
    return roles.some((r) => r === "score:score_admin" || r === "score_admin");
  } catch {
    return false;
  }
}

export default function ScoreEntitlementGate({ children }) {
  const auth = useAuth();
  const { activeOrgId, isSuper, orgResolved } = useActiveOrg();
  const { t } = useTranslation("scoreadmin");
  const accessToken = auth.user?.access_token;

  // #2120 (approved by Jaybill 2026-08-30, card comment): decide off
  // `orgResolved`, NOT the org-list network fetch (`loading`). For a
  // returning user, activeOrgId is already known synchronously from
  // localStorage (common-fe ActiveOrgContext), so this gate can check the
  // token's OWN entitlements/roles claims for that org immediately — no
  // waiting on the GET /org round trip. Accepted trade: if the active org's
  // entitlement/role was revoked since the token was minted, this can let a
  // now-unentitled user in until they next get a fresh token (logout, or
  // token refresh) — deliberately not mitigated (see ActiveOrgContext.jsx).
  // Only a brand-new session with no stored org (activeOrgId still null)
  // waits here, because there is genuinely no org to check claims against
  // yet.
  if (!orgResolved) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Super bypasses (may act on any org). Everyone else needs BOTH the org's
  // score entitlement AND the score-admin role for the active org (#531).
  if (
    isSuper ||
    (activeOrgEntitledToScore(accessToken, activeOrgId) &&
      activeOrgHasScoreAdminRole(accessToken, activeOrgId))
  ) {
    return children;
  }

  // #881 — a user whose ACTIVE org fails this check but IS entitled on
  // another org must still be able to log out and switch orgs from right
  // here: this used to render with no chrome at all (no AppBar reaches this
  // far — see App.jsx, the gate wraps AdminShell), so the only escape was
  // typing /logout. GateHeader carries the org switcher + sign-out + active
  // identity/org (so the user can see what's being denied) without needing
  // the rest of the shell (nav rail, show switcher) this screen has nothing
  // to route to anyway.
  return (
    <>
      <GateHeader onLogout={handleLogout} />
      {/* pt: 64px clears GateHeader's fixed AppBar; the rest matches the
          original spacing (py: 8). */}
      <Box
        sx={{ maxWidth: 560, mx: "auto", pt: "64px", pb: 8, px: 3, textAlign: "center" }}
      >
        <Box sx={{ pt: 8 }}>
          <Typography variant="h5" gutterBottom color="text.primary">
            {t("entitlement.gatedHeading")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("entitlement.gatedBody")}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
